import { FiltersPanelActions } from "../../../../common/components/filters/FiltersPanelActions";
import { FiltersPanelSection } from "../../../../common/components/filters/FiltersPanelSection";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import {
  ALL_HALLS_OPTION_ID,
  IPlannerFiltersState,
} from "../models/plannerFilters";
import { EventType } from "../../../../common/enums";
import { EventTypeLabels } from "../../../../common/labels";
import {
  DefaultMultiSelectDropdown,
  IMultiSelectOption,
} from "../../../../common/components/forms/inputs/dropdown/DefaultMultiSelectDropdown";
import { DefaultCheckbox } from "../../../../common/components/forms/inputs/checkbox/DefaultCheckbox";
import { DefaultToggle } from "../../../../common/components/forms/inputs/toggle/DefaultToggle";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import styles from "./PlannerFiltersSidebar.module.scss";
import { useAppContext } from "../../../../AppContext";

const ALL_HALLS_PSEUDO_OPTION: IMultiSelectOption = {
  id: ALL_HALLS_OPTION_ID,
  name: "Всички",
};

interface IPlannerFiltersSidebarProps {
  filters: IPlannerFiltersState;
  studioEmployees: ISelectFieldModel[] | undefined;
  studioHalls: ISelectFieldModel[] | undefined;
  tenantUsesMoreThanOneHall: boolean;
  onFiltersChange: (filters: Partial<IPlannerFiltersState>) => void;
  onClearAllFilters: () => void;
  onClose: () => void;
}

export const PlannerFiltersSidebar = ({
  filters,
  studioEmployees,
  studioHalls,
  tenantUsesMoreThanOneHall,
  onFiltersChange,
  onClearAllFilters,
  onClose,
}: IPlannerFiltersSidebarProps) => {
  const { showServicesTypeOptions, showGroupTypeOptions } = useAppContext();

  const eventTypes = [];

  if (showGroupTypeOptions) {
    eventTypes.push(
      { value: EventType.Group, label: EventTypeLabels[EventType.Group] },
      {
        value: EventType.Individual,
        label: EventTypeLabels[EventType.Individual],
      },
    );
  }

  if (showServicesTypeOptions) {
    eventTypes.push(
      { value: EventType.Service, label: EventTypeLabels[EventType.Service] },
      {
        value: EventType.BlockedTime,
        label: EventTypeLabels[EventType.BlockedTime],
      },
    );
  }

  const handleInstructorSelectionChange = (
    selectedOptions: IMultiSelectOption[],
  ) => {
    onFiltersChange({
      selectedInstructors: selectedOptions as ISelectFieldModel[],
    });
  };

  const selectedHallObjects: IMultiSelectOption[] =
    !filters.selectedHallIds ||
    filters.selectedHallIds.length === 0 ||
    filters.selectedHallIds.includes(ALL_HALLS_OPTION_ID)
      ? [ALL_HALLS_PSEUDO_OPTION]
      : (studioHalls ?? []).filter((h) =>
          filters.selectedHallIds.includes(h.id),
        );

  const handleHallSelectionChange = (selected: IMultiSelectOption[]) => {
    const ids =
      selected.length === 0
        ? [ALL_HALLS_OPTION_ID]
        : selected.map((o) => Number(o.id));
    onFiltersChange({ selectedHallIds: ids });
  };

  const handleEventTypeToggle = (eventType: EventType, checked: boolean) => {
    if (checked) {
      onFiltersChange({
        selectedEventTypes: [...filters.selectedEventTypes, eventType],
      });
    } else {
      onFiltersChange({
        selectedEventTypes: filters.selectedEventTypes.filter(
          (type) => type !== eventType,
        ),
      });
    }
  };

  const handleApplyFilters = () => {
    onClose();
  };

  return (
    <div className={styles.container}>
      <FiltersPanelSection title="Служители" icon={"👨‍🏫"}>
        <DefaultMultiSelectDropdown
          options={studioEmployees || []}
          selectedOptions={filters.selectedInstructors}
          onSelectionChange={handleInstructorSelectionChange}
          placeholder="Избери служители..."
          maxDisplayCount={2}
          maxWidth={"300px"}
        />
      </FiltersPanelSection>

      <RenderWhen condition={tenantUsesMoreThanOneHall}>
        <FiltersPanelSection title="Зали" icon={"🏠"} withSeparator>
          <DefaultMultiSelectDropdown
            options={studioHalls ?? []}
            selectedOptions={selectedHallObjects}
            onSelectionChange={handleHallSelectionChange}
            placeholder="Избери зали..."
            maxDisplayCount={2}
            maxWidth={"300px"}
            selectAllOption={ALL_HALLS_PSEUDO_OPTION}
          />
        </FiltersPanelSection>
      </RenderWhen>

      <FiltersPanelSection title="Типове събития" icon={"📅"} withSeparator>
        <div className={styles.checkboxContainer}>
          {eventTypes.map((eventType) => (
            <DefaultCheckbox
              key={eventType.value}
              name={`eventType_${eventType.value}`}
              label={eventType.label}
              standalone
              checked={filters.selectedEventTypes.includes(eventType.value)}
              maxWidth={"300px"}
              onChange={(checked) =>
                handleEventTypeToggle(eventType.value, checked)
              }
            />
          ))}
        </div>
      </FiltersPanelSection>

      <FiltersPanelSection title="Изтрити събития" icon={"🗑️"} withSeparator>
        <DefaultToggle
          name="showDeletedEvents"
          label="Покажи изтритите събития"
          standalone
          checked={filters.showDeletedEvents}
          maxWidth={"300px"}
          onChange={(checked) =>
            onFiltersChange({ showDeletedEvents: checked })
          }
        />
      </FiltersPanelSection>

      <FiltersPanelActions
        onApply={handleApplyFilters}
        onClearAll={onClearAllFilters}
      />
    </div>
  );
};
